import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { KanbanColumn } from '../../components/vendors/KanbanColumn'
import { Vendor } from '../../components/vendors/VendorCard'
import { Button, Input, Card, Spinner } from '../../components/ui'
import { programsAPI, type SelectionStage, type VendorRecommendation } from '../../lib/services'
import { tokenManager } from '../../lib/api'
import { Plus, X, Building2 } from 'lucide-react'
import { useViewportContext } from '../../hooks/useViewportContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// Transform API recommendation data to Vendor format for kanban
const transformRecommendationToVendor = (rec: VendorRecommendation): Vendor => {
  return {
    id: rec.vendor.id.toString(),
    submissionId: rec.recommendation_id.toString(), // used as the drag-and-drop key
    name: rec.vendor.name,
    productName: rec.product?.name || undefined,
    website: rec.vendor.website || '',
    status: rec.status,
    statusDetail: rec.product?.name || 'Recommended',
    technical: rec.technical_fit?.overall_score ? rec.technical_fit.overall_score / 20 : 0, // 0-100 → 0-5
    manufacturing: rec.scorecards?.manufacturing_readiness ? rec.scorecards.manufacturing_readiness / 20 : 0,
    vcScorecard: rec.scorecards?.vc_metrics ? rec.scorecards.vc_metrics / 20 : 0,
    dodReadiness: rec.scorecards?.dod_partnership ? rec.scorecards.dod_partnership / 20 : 0,
  }
}

// Columns interface for Kanban view
interface Column {
  id: string
  title: string
  order: number
}


type ViewMode = 'list' | 'kanban'

export default function ProgramVendors() {
  const navigate = useNavigate()
  const { programId } = useParams<{ programId: string }>()
  const [viewMode, setViewMode] = useState<ViewMode>('kanban')
  const [vendors, setVendors] = useState<Record<string, Vendor[]>>({})
  const [columns, setColumns] = useState<Column[]>([])
  const [selectionStages, setSelectionStages] = useState<SelectionStage[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Add Vendor Modal state
  const [showAddVendorModal, setShowAddVendorModal] = useState(false)
  const [vendorSearchQuery, setVendorSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [searchingVendors, setSearchingVendors] = useState(false)
  const [creatingVendor, setCreatingVendor] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [newVendorData, setNewVendorData] = useState({
    vendor_name: '',
    website: '',
    product_name: '',
    product_description: ''
  })

  // Tier 2: Register viewport serializer
  useViewportContext('program_vendors', () => {
    // Flatten all vendors across columns
    const allVendors = Object.entries(vendors).flatMap(([columnId, columnVendors]) =>
      columnVendors.map(v => ({
        id: v.id,
        name: v.name,
        status: v.status,
        column: columnId,
        technical: v.technical,
        manufacturing: v.manufacturing,
        vcScorecard: v.vcScorecard,
        dodReadiness: v.dodReadiness,
      }))
    );

    return {
      view_mode: viewMode,
      columns: columns.map(c => ({ id: c.id, title: c.title, order: c.order })),
      selection_stages: selectionStages.map(s => ({ id: s.id, name: s.name, order: s.order })),
      vendors: allVendors,
      total_vendors: allVendors.length,
    };
  }, [vendors, columns, selectionStages, viewMode]);

  // Fetch selection stages and vendor recommendations for this program
  useEffect(() => {
    const fetchData = async () => {
      if (!programId) return

      try {
        setLoading(true)
        setError(null)

        // Fetch both selection stages and vendor recommendations in parallel
        const [stages, recData] = await Promise.all([
          programsAPI.getSelectionStages(parseInt(programId)),
          programsAPI.getVendorRecommendations(parseInt(programId))
        ])

        // Store selection stages for later lookup
        setSelectionStages(stages)

        // Create columns from selection stages
        const stageColumns: Column[] = stages.map(stage => ({
          id: stage.name,
          title: stage.name,
          order: stage.order
        }))
        setColumns(stageColumns)

        // Initialize grouped object with all stage names
        const grouped: Record<string, Vendor[]> = {}
        stageColumns.forEach(col => {
          grouped[col.id] = []
        })

        // Group recommendations by selection_stage_name
        const recommendations = recData.recommendations || []
        recommendations.forEach(rec => {
          const vendor = transformRecommendationToVendor(rec)
          const stageName = rec.selection_stage_name || stageColumns[0]?.id || 'Invitations'
          if (grouped[stageName]) {
            grouped[stageName].push(vendor)
          } else {
            const firstColumn = stageColumns[0]?.id
            if (firstColumn && grouped[firstColumn]) {
              grouped[firstColumn].push(vendor)
            }
          }
        })

        setVendors(grouped)
      } catch (err) {
        console.error('Error fetching data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load program data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [programId])

  // Search vendors by name
  useEffect(() => {
    const searchVendors = async () => {
      if (vendorSearchQuery.length < 2) {
        setSearchResults([])
        return
      }

      setSearchingVendors(true)
      try {
        const token = tokenManager.getAccessToken()
        if (!token) return

        const response = await fetch(
          `${API_BASE_URL}/api/vendors/search/?q=${encodeURIComponent(vendorSearchQuery)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        )

        if (response.ok) {
          const data = await response.json()
          setSearchResults(data)
        }
      } catch (err) {
        console.error('Error searching vendors:', err)
      } finally {
        setSearchingVendors(false)
      }
    }

    const debounceTimer = setTimeout(searchVendors, 300)
    return () => clearTimeout(debounceTimer)
  }, [vendorSearchQuery])

  const handleSelectExistingVendor = async (vendorId: number) => {
    setModalError(null)

    try {
      const token = tokenManager.getAccessToken()
      if (!token) return

      // Add vendor to shortlist (creates a ProgramVendorRecommendation)
      const response = await fetch(`${API_BASE_URL}/api/programs/${programId}/shortlist/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendor_id: vendorId,
        })
      })

      if (response.ok) {
        window.location.reload()
      } else {
        const errorData = await response.json()
        setModalError(errorData.error || 'Failed to add vendor')
      }
    } catch (err) {
      console.error('Error adding existing vendor:', err)
      setModalError('Failed to add vendor')
    }
  }

  const handleCreateNewVendor = async () => {
    if (!newVendorData.vendor_name || !newVendorData.website || !newVendorData.product_name) {
      setModalError('Please fill in all required fields')
      return
    }

    setCreatingVendor(true)
    setModalError(null)

    try {
      const token = tokenManager.getAccessToken()
      if (!token) return

      // Step 1: Create vendor + product via existing endpoint
      const createResponse = await fetch(`${API_BASE_URL}/api/submissions/create-vendor-and-invite/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          program_id: parseInt(programId!),
          vendor_name: newVendorData.vendor_name,
          website: newVendorData.website,
          product_name: newVendorData.product_name,
          product_description: newVendorData.product_description,
          notes: 'Created from vendor kanban'
        })
      })

      if (!createResponse.ok) {
        const errorData = await createResponse.json()
        setModalError(errorData.error || 'Failed to create vendor')
        return
      }

      const created = await createResponse.json()

      // Step 2: Add to shortlist (creates ProgramVendorRecommendation)
      await fetch(`${API_BASE_URL}/api/programs/${programId}/shortlist/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vendor_id: created.vendor?.id,
          product_id: created.product?.id,
        })
      })

      window.location.reload()
    } catch (err) {
      console.error('Error creating vendor:', err)
      setModalError('Failed to create vendor')
    } finally {
      setCreatingVendor(false)
    }
  }

  const handleDrop = async (vendor: Vendor, sourceColumn: string, targetColumn: string) => {
    if (sourceColumn === targetColumn) return

    // Find the target SelectionStage by name
    const targetStage = selectionStages.find(stage => stage.name === targetColumn)
    if (!targetStage) {
      console.error(`Target stage "${targetColumn}" not found`)
      return
    }

    // Optimistically update UI
    setVendors((prevVendors) => {
      const newVendors = { ...prevVendors }
      
      // Remove from source
      newVendors[sourceColumn] = newVendors[sourceColumn].filter((v) => v.submissionId !== vendor.submissionId)
      
      // Update status to reflect new stage
      const updatedVendor = { ...vendor }
      updatedVendor.status = targetColumn
      updatedVendor.statusDetail = 'Moved to ' + targetColumn
      
      // Add to target
      newVendors[targetColumn] = [...newVendors[targetColumn], updatedVendor]
      
      return newVendors
    })

    // Call API to update the recommendation's selection_stage
    try {
      await programsAPI.updateRecommendationStage(parseInt(programId!), parseInt(vendor.submissionId), targetStage.id)
      console.log(`✅ Updated recommendation ${vendor.submissionId} to stage "${targetColumn}"`)
    } catch (err) {
      console.error('Failed to update recommendation stage:', err)
      
      // Revert optimistic update on error
      setVendors((prevVendors) => {
        const newVendors = { ...prevVendors }
        
        // Remove from target
        newVendors[targetColumn] = newVendors[targetColumn].filter((v) => v.submissionId !== vendor.submissionId)
        
        // Add back to source
        newVendors[sourceColumn] = [...newVendors[sourceColumn], vendor]
        
        return newVendors
      })
      
      // Show error to user
      setError(err instanceof Error ? err.message : 'Failed to update vendor stage')
      
      // Clear error after 5 seconds
      setTimeout(() => setError(null), 5000)
    }
  }

  // Flatten all vendors for list view
  const allVendors = Object.values(vendors).flat()
  const filteredVendors = allVendors.filter((vendor) =>
    vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vendor.website.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Show loading state
  if (loading) {
    return (
      <div className="h-full flex flex-col bg-background">
        <div className="flex items-center justify-center h-96">
          <Spinner size="lg" />
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="h-full flex flex-col bg-background">
        <div className="p-8">
          <Card className="p-8 text-center">
            <div className="text-destructive mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Error Loading Submissions
            </h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-border px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Vendors</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage vendors and track their progress through the evaluation pipeline
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Add Vendor Button */}
            <button
              onClick={() => setShowAddVendorModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Vendor</span>
            </button>
            
            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-slate-700 text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              List
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-white dark:bg-slate-700 text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              Kanban
            </button>
            </div>
          </div>
        </div>

        {viewMode === 'list' && (
          <div className="mt-4">
            <Input
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-md"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {viewMode === 'kanban' ? (
          <DndProvider backend={HTML5Backend}>
            <div className="p-8">
              <div className="flex gap-4 w-full overflow-x-auto pb-4">
                {columns.map((column) => (
                  <KanbanColumn
                    key={column.id}
                    id={column.id}
                    title={column.title}
                    vendors={vendors[column.id] || []}
                    onDrop={handleDrop}
                  />
                ))}
              </div>
            </div>
          </DndProvider>
        ) : (
          <div className="p-8">
            <div className="space-y-3">
              {filteredVendors.map((vendor) => (
                <Card
                  key={vendor.id}
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-500 dark:hover:border-blue-500"
                  onClick={() => navigate(`/programs/${programId}/vendors/${vendor.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="bg-slate-200 dark:bg-slate-700 rounded size-10 flex items-center justify-center shrink-0">
                        <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground">
                          {vendor.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {vendor.website}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">
                          {vendor.status}
                        </p>
                        <p className="text-sm text-card-foreground mt-0.5">
                          {vendor.statusDetail}
                        </p>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <div className="text-center">
                          <p className="text-muted-foreground text-xs">Tech</p>
                          <p className="font-semibold text-foreground">{vendor.technical.toFixed(1)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-muted-foreground text-xs">Mfg</p>
                          <p className="font-semibold text-foreground">{vendor.manufacturing.toFixed(1)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-muted-foreground text-xs">VC</p>
                          <p className="font-semibold text-foreground">{vendor.vcScorecard.toFixed(1)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-muted-foreground text-xs">DoW</p>
                          <p className="font-semibold text-foreground">{vendor.dodReadiness.toFixed(1)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
              {filteredVendors.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No vendors found</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Vendor Modal */}
      {showAddVendorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-xl font-semibold text-foreground">
                Add Vendor
              </h3>
              <button
                onClick={() => {
                  setShowAddVendorModal(false)
                  setShowCreateForm(false)
                  setVendorSearchQuery('')
                  setNewVendorData({ vendor_name: '', website: '', product_name: '', product_description: '' })
                  setModalError(null)
                }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {!showCreateForm ? (
                <>
                  {/* Search Existing Vendors */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Search for existing vendor
                    </label>
                    <input
                      type="text"
                      value={vendorSearchQuery}
                      onChange={(e) => setVendorSearchQuery(e.target.value)}
                      placeholder="Type vendor name..."
                      className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-700 text-foreground focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Search Results */}
                  {searchingVendors && (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    </div>
                  )}

                  {!searchingVendors && searchResults.length > 0 && (
                    <div className="space-y-2 mb-6">
                      <p className="text-sm font-medium text-card-foreground mb-2">
                        Found {searchResults.length} vendor{searchResults.length !== 1 ? 's' : ''}:
                      </p>
                      {searchResults.map((vendor) => (
                        <div
                          key={vendor.id}
                          className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600"
                        >
                          <div>
                            <p className="font-medium text-foreground">{vendor.name}</p>
                            {vendor.headquarters_location && (
                              <p className="text-sm text-muted-foreground">{vendor.headquarters_location}</p>
                            )}
                            {vendor.website && (
                              <p className="text-xs text-primary">{vendor.website}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleSelectExistingVendor(vendor.id)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Add
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {!searchingVendors && vendorSearchQuery.length >= 2 && searchResults.length === 0 && (
                    <div className="text-center py-4 mb-6 text-muted-foreground">
                      No vendors found matching "{vendorSearchQuery}"
                    </div>
                  )}

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-card text-muted-foreground">or</span>
                    </div>
                  </div>

                  {/* Create New Vendor Button */}
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="w-full py-3 border-2 border-dashed border-input rounded-lg hover:border-blue-400 dark:hover:border-blue-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-card-foreground font-medium flex items-center justify-center gap-2"
                  >
                    <Building2 className="w-5 h-5" />
                    <span>Create New Vendor</span>
                  </button>
                </>
              ) : (
                <>
                  {/* Create New Vendor Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-1">
                        Vendor Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newVendorData.vendor_name}
                        onChange={(e) => setNewVendorData({ ...newVendorData, vendor_name: e.target.value })}
                        placeholder="e.g., Acme Defense Systems"
                        className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-700 text-foreground focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-1">
                        Website <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={newVendorData.website}
                        onChange={(e) => setNewVendorData({ ...newVendorData, website: e.target.value })}
                        placeholder="https://example.com"
                        className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-700 text-foreground focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-1">
                        Product Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newVendorData.product_name}
                        onChange={(e) => setNewVendorData({ ...newVendorData, product_name: e.target.value })}
                        placeholder="e.g., Eagle-X Drone System"
                        className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-700 text-foreground focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-card-foreground mb-1">
                        Product Description
                      </label>
                      <textarea
                        value={newVendorData.product_description}
                        onChange={(e) => setNewVendorData({ ...newVendorData, product_description: e.target.value })}
                        placeholder="Brief description of the product..."
                        rows={3}
                        className="w-full px-4 py-2 border border-input rounded-lg bg-white dark:bg-slate-700 text-foreground focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="mt-4 text-sm text-primary hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    ← Back to search
                  </button>
                </>
              )}

              {modalError && (
                <div className="mt-4 bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                  <p className="text-red-800 dark:text-red-200 text-sm">{modalError}</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {showCreateForm && (
              <div className="border-t border-border p-4 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowAddVendorModal(false)
                    setShowCreateForm(false)
                    setNewVendorData({ vendor_name: '', website: '', product_name: '', product_description: '' })
                    setModalError(null)
                  }}
                  className="px-4 py-2 bg-slate-200 dark:bg-slate-700 text-foreground rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateNewVendor}
                  disabled={creatingVendor || !newVendorData.vendor_name || !newVendorData.website || !newVendorData.product_name}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {creatingVendor ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    'Create Vendor'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

